import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Typography, notification } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PageWrapper from '../../../ui/PageWrapper';
import { CounterpartyService } from '../../../services/CounterpartyService';
import { CounterpartyFormDto } from '../../../interfaces/CounterpartyDtos';


const CounterpartyFormsPage = () => {
    const [data, setData] = useState<CounterpartyFormDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<CounterpartyFormDto | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCounterpartyForms();
    }, []);

    const fetchCounterpartyForms = async () => {
        setLoading(true);
        try {
            const response = await CounterpartyService.getCounterpartyForms();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching data" });
        } finally {
            setLoading(false);
        }
    };


    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: CounterpartyFormDto) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this form?',
            onOk: async () => {
                setLoading(true);
                try {
                    await CounterpartyService.deleteCounterpartyForm(id);
                    setData(prev => prev.filter(item => item.id !== id));
                    notification.success({ message: "Counterparty form successfully deleted" });
                } catch (error) {
                    console.error("Failed to delete counterparty form:", error);
                    notification.error({ message: "Failed to delete" });
                } finally {
                    setLoading(false);
                }
            },
        });
    };


    const handleSave = async () => {
        try {
            const values: CounterpartyFormDto = await form.validateFields();
            setLoading(true);

            if (editingItem) {
                await CounterpartyService.updateCounterpartyForm(editingItem.id!, values);
                notification.success({ message: "Counterparty form successfully updated" });
            } else {
                await CounterpartyService.createCounterpartyForm(values);
                notification.success({ message: "Counterparty form successfully created" });
            }

            setIsModalVisible(false);
            fetchCounterpartyForms();
        } catch (error) {
            notification.error({ message: "Error saving counterparty form" });
        } finally {
            setLoading(false);
        }
    };


    const columns = [
        { title: 'Short Name (KZ)', dataIndex: 'shortNameKz', key: 'shortNameKz' },
        { title: 'Short Name (RU)', dataIndex: 'shortNameRu', key: 'shortNameRu' },
        { title: 'Short Name (EN)', dataIndex: 'shortNameEn', key: 'shortNameEn' },
        { title: 'Long Name (KZ)', dataIndex: 'longNameKz', key: 'longNameKz' },
        { title: 'Long Name (RU)', dataIndex: 'longNameRu', key: 'longNameRu' },
        { title: 'Long Name (EN)', dataIndex: 'longNameEn', key: 'longNameEn' },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: CounterpartyFormDto) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id!)} />
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Counterparty Forms</Typography.Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
                Add Counterparty Form
            </Button>
            <Table columns={columns} dataSource={data} loading={loading} rowKey="id" />
            <Modal
                title={editingItem ? 'Edit Counterparty Form' : 'Add Counterparty Form'}
                visible={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="shortNameKz" label="Short Name (KZ)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="shortNameRu" label="Short Name (RU)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="shortNameEn" label="Short Name (EN)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="longNameKz" label="Long Name (KZ)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="longNameRu" label="Long Name (RU)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="longNameEn" label="Long Name (EN)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </PageWrapper>
    );
};

export default CounterpartyFormsPage;
